import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useViewMode } from './ViewModeContext';
import { useActivation } from './ActivationContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OnboardingFlowContext = createContext();

// Walkthrough completion is keyed PER USER, not per device.
// Locked 2026-05-30 per owner directive:
//   "It should not be per device. It needs to be per sign up. Every time
//    someone signs up and they go to the dashboard it should be a walkthrough."
//
// The backend is the source of truth (/api/onboarding/progress/{user_id});
// these localStorage keys exist only as a fast-path cache to prevent a flash
// of the walkthrough overlay before the API responds. By scoping the keys
// with `:${user_id}` we guarantee every new account on the same device gets
// a fresh walkthrough.
const WALKTHROUGH_DONE_KEY    = (uid) => `walkthrough_completed:${uid || 'anon'}`;
const WALKTHROUGH_DONE_AT_KEY = (uid) => `walkthrough_completed_at:${uid || 'anon'}`;
// Doctrine 2026-09: explicit Start-or-Skip decision before the walkthrough runs.
const WALKTHROUGH_CHOICE_KEY  = (uid) => `walkthrough_choice:${uid || 'anon'}`;

// Stale-walkthrough cleanup — runs once per module load. Wipes
//   (a) legacy UN-scoped keys from older code paths, AND
//   (b) any user-scoped key whose completion timestamp is older than the
//       30-day threshold AND whose user is not currently signed in on this
//       device (so a returning user gets the walkthrough fresh after long
//       absence + sign-out).
const WALKTHROUGH_STALE_DAYS = 30;
const _runStaleCleanup = () => {
  try {
    // (a) Wipe LEGACY un-scoped keys from any prior version of the app.
    // These caused the per-device-not-per-user bug fixed 2026-05-30.
    localStorage.removeItem('walkthrough_completed');
    localStorage.removeItem('walkthrough_completed_at');

    // (b) Sweep user-scoped keys that have aged out on this signed-out device.
    const currentUid = localStorage.getItem('user_id');
    const keysToCheck = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('walkthrough_completed_at:')) keysToCheck.push(k);
    }
    keysToCheck.forEach((atKey) => {
      const uid = atKey.slice('walkthrough_completed_at:'.length);
      if (uid && uid === currentUid) return;  // skip currently-signed-in user
      const at = Number(localStorage.getItem(atKey));
      if (Number.isFinite(at) && Date.now() - at > WALKTHROUGH_STALE_DAYS * 86400000) {
        localStorage.removeItem(atKey);
        localStorage.removeItem(`walkthrough_completed:${uid}`);
      }
    });
  } catch { /* localStorage unavailable */ }
};

const PUBLIC_ROUTES = ['/', '/landing', '/login', '/signup', '/forgot-password', '/reset-password', '/terms-and-policy', '/privacy', '/terms', '/about', '/services', '/remodify', '/portal'];

// Routes where walkthrough must NEVER appear.
// NOTE: these are prefixes passed to String.startsWith — intentionally WITHOUT
// trailing slashes so that both the bare path (e.g. "/remodify") and any
// sub-path (e.g. "/remodify/abc123") are both blocked. A trailing slash here
// would let the bare path slip through on a device that still has a stale
// onboarding session in localStorage.
const BLOCKED_ROUTES = [
  '/login', '/signup', '/forgot-password', '/reset-password',
  '/terms-and-policy', '/privacy', '/terms', '/about', '/services',
  '/landing', '/portal',
  '/remodify', '/customer-portal',
  '/employee-portal', '/contractor-portal', '/pay-portal', '/scheduler-portal',
  '/super-admin', '/analytics-login', '/verify-email', '/build-experience', '/membership', '/founders-checklist',
  '/pro',
];

// Only these roles may see the walkthrough.
//
// Locked 2026-06-01 — Onboarding Consolidation Phase A (Step 5).
// The canonical eligibility lives in the backend
// (services/activation_state.WALKTHROUGH_ELIGIBLE_ROLES_PROVIDER) and is
// returned by /api/auth/me as `walkthrough_eligible` + `walkthrough_kind`.
// The hardcoded list below is now a FALLBACK ONLY — used when the
// ActivationContext hasn't resolved yet (paint-time race). Once the
// context loads, the live `walkthroughEligible` flag is the source of
// truth. This closes the cross-overlay disagreement bug (mobile said
// ['admin','owner','super_admin'], desktop said
// ['admin','business_owner','contractor'] — non-overlapping lists).
const FALLBACK_WALKTHROUGH_ROLES = ['admin', 'business_owner', 'owner', 'provider'];

export const useOnboardingFlow = () => {
  const ctx = useContext(OnboardingFlowContext);
  if (!ctx) return {
    isActive: false, isHardGated: false, currentStep: null,
    currentStepId: null, completedSteps: [], progress: null,
    completeStep: () => {}, acknowledgeStep: () => {},
    handleSwipeDetected: () => {}, canNavigate: () => true,
    setupProgress: { completed: 0, total: 3, percent: 0 },
    resetOnboarding: () => {}, skipWalkthrough: () => {}, isMobile: false,
    showStartPrompt: false, startWalkthroughNow: () => {}, declineWalkthrough: () => {},
  };
  return ctx;
};

// Steps that MUST be completed before app is unlocked
const HARD_GATE_STEPS = ['business_profile', 'branding', 'remodpay_setup'];

// Setup progress items for Mission Control
export const SETUP_STEPS = [
  { id: 'business_profile', label: 'Complete Business Profile', desc: 'Add your personal & business details', icon: 'user' },
  { id: 'branding', label: 'Add Branding', desc: 'Upload logo and company identity', icon: 'palette' },
  { id: 'remodpay_setup', label: 'Set Up Payments', desc: 'Connect payments to get paid', icon: 'dollar' },
];

// ═══════════════════════════════════════════════════
// 22-STEP FLOW — exact order, strictly sequential
// ═══════════════════════════════════════════════════
// action types:
//   'acknowledge' = tap "Got it" or overlay to proceed (highlight only, no navigation)
//   'navigate'    = user must tap target and navigate to a route
//   'save'        = wait for real save event (window custom event)
//   'swipe'       = wait for actual swipe gesture
const STEPS = [
  // Streamlined tap-through tour (owner directive 2026-08: "seamless, simple,
  // quick — tell you everything you need to know and then go back to the home
  // screen"). Every step is a one-tap acknowledge ON the home screen — no
  // forced swipes, no save-gated steps, no route hops. Ends back at home.
  { id: 'welcome', phase: 1, text: "Welcome to RemodDash —\nhere's a quick tour of your workspace.", target: null, action: 'acknowledge' },
  { id: 'gesture_swipe_ari', phase: 2, text: "Swipe up here anytime to talk to Ari —\nyour AI operator.", target: '[data-testid="footer-ari-zone"]', action: 'acknowledge' },
  { id: 'gesture_swipe_quick', phase: 2, text: "Swipe up here for quick actions.", target: '[data-testid="footer-quick-zone"]', action: 'acknowledge' },
  { id: 'gesture_nav_bar', phase: 2, text: "Use this bar to move around the app.\nHold the home button to log out.", target: '[data-testid="mobile-footer-nav"]', action: 'acknowledge' },
  { id: 'dispatch_highlight', phase: 3, text: "This is Dispatch —\nyour jobs and schedule.", target: '[data-testid="module-dispatch"]', action: 'acknowledge' },
  { id: 'qi_highlight', phase: 3, text: "Quotes and invoices live here.", target: '[data-testid="module-remodquotes"]', action: 'acknowledge' },
  { id: 'remodpay_highlight', phase: 3, text: "Payments — get paid here.", target: '[data-testid="module-remodpay"]', action: 'acknowledge' },
  { id: 'modules_overview', phase: 3, text: "Everything else lives in your modules.\nSet up your business profile, branding\nand payments in Settings when you're ready.", target: '[data-testid="module-grid"]', action: 'acknowledge' },
  { id: 'complete', phase: 4, text: "That's it — you're ready.\nThis is your home screen.", target: null, action: 'acknowledge' },
];

const STEP_IDS = STEPS.map(s => s.id);

export const OnboardingFlowProvider = ({ children }) => {
  const location = useLocation();
  const { isMobile: viewModeIsMobile } = useViewMode();
  const [progress, setProgress] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const userId = useRef(null);
  const loadedRef = useRef(false);

  // Iter184 P2.4 — clear stale walkthrough completion flag on
  // signed-out devices after 30 days. Run once when the provider mounts.
  const cleanupRanRef = useRef(false);
  if (!cleanupRanRef.current) {
    cleanupRanRef.current = true;
    _runStaleCleanup();
  }

  // isMobile is now driven by ViewModeContext.isMobile, NOT raw
  // window.innerWidth. This means toggling to Desktop view via
  // ViewModeToggle immediately deactivates the mobile walkthrough,
  // even on small physical viewports. Locked 2026-05-31 — User-Blocker
  // Pass (Blocker #2: "Swipe left instructions on desktop is wrong").
  // We also keep a window.innerWidth fallback for pre-Provider mounts.
  useEffect(() => {
    setIsMobile(viewModeIsMobile);
  }, [viewModeIsMobile]);

  const loadProgress = useCallback(async () => {
    const uid = localStorage.getItem('user_id');
    if (!uid) return;
    userId.current = uid;
    try {
      const res = await axios.get(`${API}/onboarding/progress/${uid}`);
      setProgress(res.data);
      // Sync persistent completion flag from backend — USER-SCOPED.
      // is_complete OR skipped_walkthrough (permanent skip) both terminate
      // the walkthrough lifecycle. Locked 2026-05-30 — Walkthrough Trap Fix.
      if (res.data.is_complete || res.data.skipped_walkthrough) {
        localStorage.setItem(WALKTHROUGH_DONE_KEY(uid), 'true');
        localStorage.setItem(WALKTHROUGH_DONE_AT_KEY(uid), String(Date.now()));
      }
    } catch {
      setProgress(null);
    }
    loadedRef.current = true;
  }, []);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  // Core completeStep — advances state machine via backend
  const completeStep = useCallback(async (stepId) => {
    const uid = userId.current || localStorage.getItem('user_id');
    if (!uid) return;
    try {
      const res = await axios.post(`${API}/onboarding/progress/${uid}/complete-step`, { step_id: stepId });
      const data = res.data;
      if (data.error) return;
      const newState = {
        ...progress,
        completed_steps: [...new Set([...(progress?.completed_steps || []), stepId])],
        current_step: data.next_step || 'complete',
        current_step_index: data.next_step_index ?? ((progress?.current_step_index || 0) + 1),
        is_complete: data.is_complete,
      };
      setProgress(newState);
      // Persist completion flag immediately when walkthrough finishes — USER-SCOPED.
      if (data.is_complete) {
        localStorage.setItem(WALKTHROUGH_DONE_KEY(uid), 'true');
        localStorage.setItem(WALKTHROUGH_DONE_AT_KEY(uid), String(Date.now()));
      }
    } catch (e) {
      console.error('Onboarding completeStep failed:', e);
    }
  }, [progress]);

  // Listen for save-type completion events from settings/dispatch pages
  useEffect(() => {
    const handler = (e) => {
      const { stepId } = e.detail || {};
      if (stepId && STEP_IDS.includes(stepId)) {
        completeStep(stepId);
      }
    };
    window.addEventListener('onboarding-step-complete', handler);
    return () => window.removeEventListener('onboarding-step-complete', handler);
  }, [completeStep]);

  // ── Derived state ──
  // Canonical eligibility — read from ActivationContext when available.
  // Step 5 (2026-06-01): single source of truth aligned with backend
  // services/activation_state.is_walkthrough_eligible.
  const {
    walkthroughEligible: ctxEligible,
    walkthroughKind: ctxKind,
    loading: ctxLoading,
    authenticated: ctxAuthenticated,
  } = useActivation();

  const isOnPublicRoute = PUBLIC_ROUTES.some(r =>
    r === '/' ? location.pathname === '/' : location.pathname.startsWith(r)
  );

  // Check BLOCKED routes (customer portal, technician portal, contractor portal, auth screens)
  const isOnBlockedRoute = BLOCKED_ROUTES.some(r => location.pathname.startsWith(r)) || location.pathname === '/';

  const hasUser = !!localStorage.getItem('user_id');

  // Walkthrough is Provider-kind only (the 22-step mobile flow). Delegated
  // roles use the informational overlay instead — that's a separate
  // component. If ActivationContext is still loading, fall back to the
  // legacy localStorage check so we don't flicker the overlay off during
  // initial hydration.
  const isBusinessOwner = (() => {
    if (!ctxLoading && ctxAuthenticated) {
      return ctxEligible && ctxKind === 'provider';
    }
    // Fallback path — ctx still loading or unauthenticated.
    try {
      const rolesStr = localStorage.getItem('user_roles');
      if (rolesStr) {
        const roles = JSON.parse(rolesStr);
        return roles.some(r => FALLBACK_WALKTHROUGH_ROLES.includes(r));
      }
    } catch { /* fallthrough */ }
    const single = localStorage.getItem('user_role');
    return FALLBACK_WALKTHROUGH_ROLES.includes(single);
  })();

  // Persistent completion flag — prevents auto-run on subsequent logins.
  // USER-SCOPED so a new account on the same device gets a fresh walkthrough
  // (locked 2026-05-30 per owner directive — per sign-up, not per device).
  const currentUid = hasUser ? localStorage.getItem('user_id') : null;
  const walkthroughAlreadyCompleted = currentUid
    ? localStorage.getItem(WALKTHROUGH_DONE_KEY(currentUid)) === 'true'
    : false;

  // Active ONLY on mobile, authenticated, business/owner, non-blocked route, incomplete progress, not already completed
  // Demo Mode owns the screen — never fire the mobile walkthrough over it.
  const isDemoMode = typeof window !== 'undefined' && localStorage.getItem('demo_mode') === 'provider';
  const eligibleForWalkthrough = !isDemoMode && isMobile && hasUser && isBusinessOwner && !isOnBlockedRoute && !isOnPublicRoute
    && progress && !progress.is_complete && !progress.skipped_walkthrough
    && !progress.skipped_this_session
    && !walkthroughAlreadyCompleted && loadedRef.current;

  // ── Doctrine 2026-09 (§4): the walkthrough never auto-starts. The provider
  // gets ONE explicit prompt — Start Walkthrough or Skip Walkthrough. Skip is
  // permanent (server-persisted); no automatic future prompts. Ari can
  // relaunch it only when the provider asks ('ari-start-walkthrough' event).
  const [walkthroughChoice, setWalkthroughChoice] = useState(null);
  useEffect(() => {
    if (currentUid) setWalkthroughChoice(localStorage.getItem(WALKTHROUGH_CHOICE_KEY(currentUid)));
  }, [currentUid]);

  const showStartPrompt = !!(eligibleForWalkthrough && !walkthroughChoice);
  const isActive = eligibleForWalkthrough && walkthroughChoice === 'started';

  const completedSteps = progress?.completed_steps || [];
  const currentStepId = progress?.current_step || 'welcome';
  const currentStep = STEPS.find(s => s.id === currentStepId) || null;

  // STRICT LOCK — the entire walkthrough is locked, not just the 3 hard-gate
  // steps. Any non-walkthrough interaction (tile taps, free swipes, route
  // changes) is blocked until is_complete=true. Locked 2026-06-02 per owner
  // directive: "Every module should remain locked until the guided
  // walkthrough is completed."
  const isWalkthroughLocked = isActive;
  // Legacy alias — kept so MissionControl etc. don't break. Equivalent now.
  const isHardGated = isWalkthroughLocked;

  // ── Route-change detection for navigate steps ──
  useEffect(() => {
    if (!isActive || !currentStep) return;

    if (currentStep.action === 'navigate' && currentStep.route) {
      if (location.pathname.startsWith(currentStep.route)) {
        const t = setTimeout(() => completeStep(currentStepId), 200);
        return () => clearTimeout(t);
      }
    }
  }, [location.pathname, isActive, currentStep, currentStepId, completeStep]);

  const acknowledgeStep = useCallback((stepId) => {
    completeStep(stepId);
  }, [completeStep]);

  const handleSwipeDetected = useCallback((dir) => {
    if (!currentStep) return;
    if (currentStep.action === 'swipe') {
      const expected = currentStep.swipeDir || 'left';
      if (dir === expected) {
        // Tell the dashboard to actually advance the page — the overlay
        // captured the touch so the dashboard's own onTouchEnd never
        // fires. Locked 2026-06-04 — Walkthrough Fix Pass #7.
        try {
          window.dispatchEvent(new CustomEvent('walkthrough:page-swipe', { detail: { direction: dir } }));
        } catch { /* SSR safety */ }
        completeStep(currentStepId);
      }
    }
  }, [currentStep, currentStepId, completeStep]);

  const canNavigate = useCallback((path) => {
    if (!isWalkthroughLocked) return true;
    if (!currentStep) return true;
    // Always allow dashboard so the user can return home from anywhere.
    if (path === '/dashboard') return true;
    // Allow the step's explicit target route.
    if (currentStep.route && path.startsWith(currentStep.route)) return true;
    // Settings phase: allow /settings and /company-branding.
    const settingsSteps = ['settings_highlight', 'business_profile', 'branding', 'remodpay_setup'];
    if (settingsSteps.includes(currentStepId)
        && (path.startsWith('/settings') || path === '/company-branding')) return true;
    // Dispatch phase: allow /jobs.
    const dispatchSteps = ['dispatch_entry', 'add_customer_button', 'create_customer'];
    if (dispatchSteps.includes(currentStepId) && path.startsWith('/jobs')) return true;
    if (currentStepId === 'return_home') return true;
    return false;
  }, [isWalkthroughLocked, currentStep, currentStepId]);

  // ─── Strict module-tap gate ───
  // Returns true ONLY when the walkthrough explicitly expects the user to
  // tap this module right now (i.e., the current step's target is the
  // module's tile). Everything else is locked while the walkthrough runs.
  const canTapModule = useCallback((moduleId) => {
    if (!isWalkthroughLocked) return true;
    if (!currentStep) return true;
    const targetSel = currentStep.target || '';
    // Step's target selector is the module's tile testid: [data-testid="module-{id}"]
    if (targetSel === `[data-testid="module-${moduleId}"]`) return true;
    // Also allow when the step explicitly routes through this module.
    if (moduleId === 'dispatch' && currentStep.route === '/jobs') return true;
    if (moduleId === 'settings' && currentStep.route === '/settings') return true;
    return false;
  }, [isWalkthroughLocked, currentStep]);

  // ─── Strict horizontal-swipe gate ───
  // Page swipes are only allowed when the current walkthrough step is a
  // swipe step AND the direction matches. All other free swipes are
  // blocked so the user cannot wander into RemodPay / Reports / etc.
  const canSwipeToPage = useCallback((direction) => {
    if (!isWalkthroughLocked) return true;
    if (!currentStep) return true;
    if (currentStep.action !== 'swipe') return false;
    const expected = currentStep.swipeDir || 'left';
    return direction === expected;
  }, [isWalkthroughLocked, currentStep]);

  // Helper for dashboard/footer to dispatch a "blocked" banner that the
  // OnboardingOverlay listens for. Centralised so the message is consistent.
  const emitBlocked = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('onboarding:blocked'));
    } catch { /* SSR safety */ }
  }, []);

  // Setup progress for Mission Control
  const setupStepsCompleted = SETUP_STEPS.filter(s => completedSteps.includes(s.id)).length;
  const setupProgress = {
    completed: setupStepsCompleted,
    total: SETUP_STEPS.length,
    percent: Math.round((setupStepsCompleted / SETUP_STEPS.length) * 100),
  };
  const activeSetupStep = SETUP_STEPS.find(s => !completedSteps.includes(s.id))?.id || null;

  const resetOnboarding = useCallback(async () => {
    const uid = userId.current || localStorage.getItem('user_id');
    if (!uid) return;
    await axios.post(`${API}/onboarding/progress/${uid}/reset`, {}).catch(() => {});
    // Clear persistent completion flag (USER-SCOPED) so walkthrough can re-run
    localStorage.removeItem(WALKTHROUGH_DONE_KEY(uid));
    localStorage.removeItem(WALKTHROUGH_DONE_AT_KEY(uid));
    setProgress(null);
    loadedRef.current = false;
    await loadProgress();
  }, [loadProgress]);

  // Skip the walkthrough — "Skip for now" (permanent=false) hides it for
  // the current session only; "Don't show again" (permanent=true) marks
  // the walkthrough complete on the backend + writes an Ari journal entry.
  // Locked 2026-05-30 — Walkthrough Trap Fix.
  const skipWalkthrough = useCallback(async (permanent = true) => {
    const uid = userId.current || localStorage.getItem('user_id');
    if (!uid) return;
    try {
      const res = await axios.post(
        `${API}/onboarding/progress/${uid}/skip`,
        { permanent: !!permanent },
      );
      setProgress((prev) => ({
        ...(prev || {}),
        is_complete: !!res.data.is_complete,
        skipped_walkthrough: !!res.data.skipped_walkthrough,
        skipped_this_session: !!res.data.skipped_this_session,
        current_step: res.data.is_complete ? 'complete' : (prev?.current_step || 'welcome'),
      }));
      if (permanent) {
        localStorage.setItem(WALKTHROUGH_DONE_KEY(uid), 'true');
        localStorage.setItem(WALKTHROUGH_DONE_AT_KEY(uid), String(Date.now()));
      }
    } catch (e) {
      console.error('Onboarding skipWalkthrough failed:', e);
    }
  }, []);

  // ── Doctrine 2026-09 (§4) — explicit prompt decisions ──
  const startWalkthroughNow = useCallback(() => {
    const uid = userId.current || localStorage.getItem('user_id');
    if (!uid) return;
    localStorage.setItem(WALKTHROUGH_CHOICE_KEY(uid), 'started');
    setWalkthroughChoice('started');
  }, []);

  const declineWalkthrough = useCallback(async () => {
    const uid = userId.current || localStorage.getItem('user_id');
    if (!uid) return;
    localStorage.setItem(WALKTHROUGH_CHOICE_KEY(uid), 'skipped');
    setWalkthroughChoice('skipped');
    await skipWalkthrough(true);
  }, [skipWalkthrough]);

  // Ari relaunches the walkthrough ONLY on explicit provider request.
  useEffect(() => {
    const handler = async () => {
      const uid = userId.current || localStorage.getItem('user_id');
      if (!uid) return;
      localStorage.setItem(WALKTHROUGH_CHOICE_KEY(uid), 'started');
      setWalkthroughChoice('started');
      await resetOnboarding();
    };
    window.addEventListener('ari-start-walkthrough', handler);
    return () => window.removeEventListener('ari-start-walkthrough', handler);
  }, [resetOnboarding]);

  return (
    <OnboardingFlowContext.Provider value={{
      isActive: !!isActive,
      isHardGated: !!isHardGated,
      isWalkthroughLocked: !!isWalkthroughLocked,
      currentStep, currentStepId, progress, completedSteps,
      completeStep, acknowledgeStep, handleSwipeDetected, canNavigate,
      canTapModule, canSwipeToPage, emitBlocked,
      setupProgress, activeSetupStep, resetOnboarding, skipWalkthrough, isMobile,
      showStartPrompt, startWalkthroughNow, declineWalkthrough,
    }}>
      {children}
    </OnboardingFlowContext.Provider>
  );
};
